import { Inject, Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  doc,
  docData,
  DocumentData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  Query,
  query,
  QueryConstraint,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { ENVIRONMENT } from '@core/core.constants';
import { Entity } from '@core/models/common.model';
import { convertTimestampsToDates } from '@core/utils/convertTimestamps';
import { forkJoin, map, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdminFirestoreService {
  private documentsReaded = 0;

  constructor(
    private firetore: Firestore,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(ENVIRONMENT) private environment: any,
  ) {}

  getCollectionRef(path: string): CollectionReference<DocumentData> {
    return collection(this.firetore, path);
  }

  generateId(path: string): string {
    const col = this.getCollectionRef(path);
    return doc(col).id;
  }

  getDocumentRef<T = DocumentData>(path: string, id: string): DocumentReference<T> {
    const col = this.getCollectionRef(path);
    return doc(col, id) as DocumentReference<T>;
  }

  getDocument<T extends Entity>(collection: string, id: string): Observable<T> {
    const doc = this.getDocumentRef(collection, id) as DocumentReference<T>;
    return docData<T>(doc).pipe(
      tap(() => this.countDatabaseReads(1, [collection, id])),
      map((res: T | undefined) => {
        if (!res) {
          throw new Error(`Document "${id}" does not exist on "${collection}"`);
        }
        if (res.deleted) {
          throw new Error(`Document "${id}" from "${collection}" is deleted.`);
        }
        return convertTimestampsToDates(res);
      }),
    );
  }

  getDocumentOrNull<T extends Entity>(collection: string, id: string): Observable<T | null> {
    const docRef = this.getDocumentRef(collection, id) as DocumentReference<T>;
    return docData<T>(docRef).pipe(
      tap(() => this.countDatabaseReads(1, [collection, id])),
      map((res: T | undefined) => {
        if (!res || res.deleted) return null;
        return convertTimestampsToDates(res);
      }),
    );
  }

  getAllDocuments<T extends Entity>(collection: string, ids: string[]): Observable<T[]> {
    return forkJoin(ids.map((id) => this.getDocument<T>(collection, id)));
  }

  listDocuments<T extends Entity>(path: string, ...queryConstraints: QueryConstraint[]): Observable<T[]> {
    const collection = this.getCollectionRef(path);
    const q = query<T, DocumentData>(collection as unknown as Query<T>, ...queryConstraints);
    return collectionData<T>(q).pipe(
      tap((d) => this.countDatabaseReads(d.length, [path, ...queryConstraints])),
      map((el) => convertTimestampsToDates(el)),
    );
  }

  listDocumentsWithId<T extends Entity>(path: string, ...queryConstraints: QueryConstraint[]): Observable<T[]> {
    const collection = this.getCollectionRef(path);
    const q = query<T, DocumentData>(collection as unknown as Query<T>, ...queryConstraints);
    return collectionData<T>(q, {
      idField: 'id',
    }).pipe(
      tap((d) => this.countDatabaseReads(d.length, [path, ...queryConstraints])),
      map((el) => convertTimestampsToDates(el)),
    );
  }

  async listDocumentsWithIdOnce<T extends Entity>(path: string, ...queryConstraints: QueryConstraint[]): Promise<T[]> {
    const collection = this.getCollectionRef(path);
    const q = query<T, DocumentData>(collection as unknown as Query<T>, ...queryConstraints);
    const snapshot = await getDocs(q);
    this.countDatabaseReads(snapshot.size, [path, ...queryConstraints]);
    return snapshot.docs.map((doc) => convertTimestampsToDates({ ...doc.data(), id: doc.id } as T));
  }

  listDocumentsGroup<T extends Entity>(groupId: string, ...queryConstraints: QueryConstraint[]): Observable<T[]> {
    const q = query<T, DocumentData>(collectionGroup(this.firetore, groupId) as Query<T>, ...queryConstraints);
    return collectionData<T>(q, {
      idField: 'id',
    }).pipe(
      tap((d) => this.countDatabaseReads(d.length, [groupId, ...queryConstraints])),
      map((el) => convertTimestampsToDates(el)),
    );
  }

  exists(collection: string, id: string): Promise<boolean> {
    const doc = this.getDocumentRef(collection, id);
    this.countDatabaseReads(1, ['exists', id]);
    return getDoc(doc)
      .then((doc) => doc.exists())
      .catch(() => false);
  }

  async create<T extends Entity>(collection: string, data: Partial<T>): Promise<string> {
    const dbData = data;
    dbData.createdAt = new Date();
    if (!dbData.id) {
      dbData.id = this.generateId(collection);
    }
    const col = this.getCollectionRef(collection);
    const document = doc(col, dbData.id);
    await setDoc<DocumentData, DocumentData>(document, dbData);
    return dbData.id;
  }

  async createOrUpdate<T extends Entity>(collection: string, id: string, data: Partial<T>): Promise<void> {
    if (data.id && data.id !== id) {
      throw new Error('Document Id mismatch from data id');
    }
    const dbData = data;
    const exists = await this.exists(collection, id);
    if (!exists) {
      dbData.createdAt = new Date();
    }
    dbData.id = id;
    dbData.updatedAt = new Date();
    const doc = this.getDocumentRef(collection, id);
    return setDoc<DocumentData, DocumentData>(doc, dbData, { merge: true });
  }

  /**
   * Cambia solo los campos indicados, sin tocar nada más del documento. Admite rutas con punto
   * ('pilot.passport'), que Firestore resuelve dentro del mapa sin reescribir el resto de sus
   * campos. Lo que no se nombra aquí se queda exactamente como estaba.
   */
  async updateFields(collection: string, id: string, fields: Record<string, unknown>): Promise<void> {
    const doc = this.getDocumentRef(collection, id);
    return updateDoc<DocumentData, DocumentData>(doc, { ...fields, updatedAt: new Date() });
  }

  async update<T extends Entity>(collection: string, data: Partial<T>): Promise<void> {
    const dbData = data;
    const id = data.id;
    if (!id) throw new Error('Document data does not contain ID');
    dbData.updatedAt = new Date();
    const doc = this.getDocumentRef(collection, id);
    return updateDoc<DocumentData, DocumentData>(doc, dbData);
  }

  async delete(collection: string, id: string): Promise<void> {
    return this.update(collection, { id, deleted: true });
  }

  async deleteForever(collection: string, id: string): Promise<void> {
    const doc = this.getDocumentRef(collection, id);
    return deleteDoc(doc);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private countDatabaseReads(length: number, data: any[] = []) {
    if (this.environment.production) return;
    const newCount = this.documentsReaded + length;
    // eslint-disable-next-line no-restricted-syntax
    console.debug(
      '%c[FirestoreService]',
      'background: #F9CB36; color: #000',
      `Document reads: ${this.documentsReaded} (+${length}) => ${newCount}`,
      data,
    );
    this.documentsReaded = newCount;
  }
}
